import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Popover from '@rio-cloud/rio-uikit/Popover';

import {
    CollectingDataBanner,
    CRITICAL,
    LOW_MILEAGE,
    NO_DATA,
    NoData,
    OK,
    tooltipMarginClassName,
    tooltipSpacerClassNames,
    tooltipWrapperClassNames,
    WARNING,
} from '@/features/batteryOverview/utils/BatteryDataUtils';
import { Criticality, SidebarStatusBar } from './SidebarStatusBar';

type ExpanderTitleType = {
    systemStateOfHealthStatus: string;
    percentage: number | undefined;
};

const ExpanderTitle = ({ systemStateOfHealthStatus, percentage }: ExpanderTitleType) => {
    const rioglyphOk = 'rioglyph rioglyph-ok';
    const hasValidSohStatus = [CRITICAL, OK, WARNING].includes(systemStateOfHealthStatus);

    const sohTooltip = useCallback(() => {
        const tooltipBodyId =
            'intl-msg:batteryService.stateOfHealth.tooltip.' + systemStateOfHealthStatus.toLowerCase();
        const tooltipNoData = 'intl-msg:batteryService.stateOfHealth.tooltip.no_data';

        const renderTooltipContent = () => (
            <>
                {systemStateOfHealthStatus !== LOW_MILEAGE && (
                    <span className="margin-bottom-10">
                        <FormattedMessage
                            id={hasValidSohStatus ? tooltipBodyId : tooltipNoData}
                            values={{
                                b: (chunks) => <strong>{chunks}</strong>,
                            }}
                        />
                    </span>
                )}
                {(systemStateOfHealthStatus === CRITICAL || systemStateOfHealthStatus === WARNING) && (
                    <ul className="margin-bottom-10">
                        <li>
                            <FormattedMessage id="intl-msg:batteryService.batteryStateOfHealth.tooltip.body" />
                        </li>
                        <li>
                            <FormattedMessage id="intl-msg:batteryService.batteryStateOfHealth.tooltip.body2" />
                        </li>
                    </ul>
                )}
            </>
        );

        return (
            <Popover className={tooltipMarginClassName} placement={'auto'} id={'soh-popover'}>
                <div className={tooltipWrapperClassNames}>
                    <div className={'h6'}>
                        <FormattedMessage id="intl-msg:batteryService.stateOfHealth" />
                    </div>
                    <hr className={tooltipSpacerClassNames} />
                    {renderTooltipContent()}
                    <span>
                        <FormattedMessage
                            id="intl-msg:batteryService.stateOfHealth.tooltip.information"
                            values={{
                                b: (chunks) => <strong>{chunks}</strong>,
                            }}
                        />
                    </span>
                </div>
            </Popover>
        );
    }, [systemStateOfHealthStatus]);

    let sohStatusClasses: string;
    let sohCriticality: Criticality;

    switch (systemStateOfHealthStatus) {
        case CRITICAL:
            sohStatusClasses = 'rioglyph rioglyph-error-sign text-color-danger margin-right-10';
            sohCriticality = Criticality.Critical;
            break;
        case WARNING:
            sohStatusClasses = 'rioglyph rioglyph-warning-sign text-color-warning margin-right-10';
            sohCriticality = Criticality.Warning;
            break;
        case NO_DATA:
            sohStatusClasses = rioglyphOk;
            sohCriticality = Criticality.Unknown;
            break;
        default:
            sohStatusClasses = `${rioglyphOk} text-color-info margin-right-10`;
            sohCriticality = Criticality.Normal;
    }

    const getCriticality = () => {
        if (hasValidSohStatus) {
            if (sohCriticality === Criticality.Critical) return 'danger';
            // OK maps to the info (darker blue) left accent
            else if (sohCriticality === Criticality.Normal) return 'info';
            else return sohCriticality;
        } else return Criticality.Unknown;
    };

    const titleClasses = 'width-50pct margin-0';
    const containerTitleClassNames = `display-flex list-group-item-${getCriticality()} 
    text-color-darkest text-normal list-group-item rounded-none border-none`;

    const renderSoHContent = () => {
        if (hasValidSohStatus)
            return (
                <div className={'width-50pct'}>
                    <SidebarStatusBar percentage={percentage} criticality={sohCriticality} />
                </div>
            );
        else if (systemStateOfHealthStatus === LOW_MILEAGE) return <CollectingDataBanner textSize={14} />;
        else return <NoData textSize={14} />;
    };

    return (
        <div className={containerTitleClassNames}>
            <div className={titleClasses}>
                <div className={'text-color-darkest'}>
                    {hasValidSohStatus ? (
                        <span className={sohStatusClasses} />
                    ) : (
                        // Keep the icon's horizontal space so the label stays aligned
                        // with the OK/warning/critical rows, but render no icon.
                        // Width matches a rioglyph (1em) so the label lines up exactly.
                        <span className="display-inline-block margin-right-10" style={{ width: '1em' }} />
                    )}
                    <span>
                        <FormattedMessage id={'intl-msg:batteryService.stateOfHealth'} />
                    </span>

                    <span className={'margin-left-5 text-color-dark'}>
                        <OverlayTrigger overlay={sohTooltip()} placement={'right'}>
                            <span className="rioglyph rioglyph-info-sign margin-left-5" />
                        </OverlayTrigger>
                    </span>
                </div>
            </div>
            {renderSoHContent()}
        </div>
    );
};

export default ExpanderTitle;
